import { mat3, ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { Observable } from '../core/observable';
import { ViewInvalidator } from './ViewInvalidator';
import { LayerBit } from './layers/LayerBit';
import { PlaneView } from './views/PlaneView';
import { RotatedBoundingBox } from '../data/common/RotatedBoundingBox';
import { BoundingBox } from '../data/common/BoundingBox';

/**
 * {@link PanZoom} is used to apply additional translation and scaling to {@link PlaneView PlaneViews}.\
 * It can be used to easily manipulate the center coordinates and scaling of {@link PlaneView PlaneViews}.\
 * Changes made to {@link PanZoom._scale scale} and {@link PanZoom._translation translation} of the {@link PanZoom}
 * {@link ViewInvalidator.invalidate invalidate} all layers, so the {@link PlaneView PlaneViews} are updated accordingly.
 */
export class PanZoom extends Observable<number> {
    /**
     * Multiplier controlling how much the scaling should change as a response to each {@link scaleIn}-method call.\
     * Generally it is good idea to have this as an inverse of {@link SCALE_OUT} so the scaling scales the same amount, just in the opposite direction.
     */
    private static readonly SCALE_IN = 1.15;

    /**
     * Multiplier controlling how much the scaling should change as a response to each {@link scaleOut}-method call.\
     * Generally it is good idea to have this as an inverse of {@link SCALE_IN} so the scaling scales the same amount, just in the opposite direction.
     */
    private static readonly SCALE_OUT = 1 / PanZoom.SCALE_IN;
    private _scale: number;

    /**
     * Boolean to determine if the zoom should be focused on the center of the view or the cursor position.\
     * Defaults to true.
     */
    private _zoomToCursor: boolean = true;

    /**
     * Constructor to create a new {@link PanZoom}-object.
     * It may be provided with initial {@link translation} and {@link scaling} values if so desired.
     * @param invalidator The {@link ViewInvalidator} whose views this {@link PanZoom} repaints.
     * @param scaling The initial {@link _scale scale} of this {@link PanZoom}-object. Defaults to 1.
     */
    public constructor(
        private readonly invalidator: ViewInvalidator,
        scaling = 1
    ) {
        super();
        this._scale = scaling;
    }

    /**
     * Fits every view in `views` to `bb`, each in its own plane, and moves
     * the crosshair to `intersectionPoint`. The first view leads: its fit
     * sets the zoom scale every view takes, and with `snapToGrid` the
     * crosshair snaps through its plane to the nearest image slice centre.
     * Moves nothing for no views or an empty box.
     */
    private applyFocus(
        views: readonly PlaneView[],
        bb: RotatedBoundingBox,
        intersectionPoint: ReadonlyVec3,
        snapToGrid: boolean
    ): void {
        // A view that follows another's geometry cannot lead the fit.
        const [lead, ...rest] = [...views].sort(
            (a, b) => followsRank(a) - followsRank(b)
        );
        if (!lead || bb.volume <= 0) {
            return;
        }

        const zoomChange = lead.fitToView(bb);
        lead.updatePlaneIntersectionPoint(intersectionPoint);
        if (snapToGrid) {
            lead.snapToImagePixel();
        }

        for (const view of rest) {
            view.fitToView(bb);
        }

        this._scale = zoomChange;
        for (const view of views) {
            view.resetCurrentScale(this._scale);
        }

        this.invalidator.invalidate(LayerBit.ALL_LAYERS);
    }

    /**
     * Focus `views` on a rotated bounding box, snapping the crosshair to the
     * primary image grid through the plane of the first view. Moves nothing
     * for no views or an empty box.
     */
    public focusViewsTo(
        views: readonly PlaneView[],
        bb: RotatedBoundingBox
    ): void {
        this.applyFocus(views, bb, bb.patientSpaceCenter, true);
    }

    /**
     * Focus `views` on a patient-space point (mm), fitting each to a cube of
     * `focusSizeMm` around it. Does not snap to the primary image grid,
     * preserving the exact target position. Moves nothing for no views.
     */
    public focusViewsToPoint(
        views: readonly PlaneView[],
        point: ReadonlyVec3,
        focusSizeMm: number = 30
    ): void {
        const halfSize = focusSizeMm / 2;
        const min = vec3.fromValues(
            point[0] - halfSize,
            point[1] - halfSize,
            point[2] - halfSize
        );
        const max = vec3.fromValues(
            point[0] + halfSize,
            point[1] + halfSize,
            point[2] + halfSize
        );
        const bb = new RotatedBoundingBox(
            new BoundingBox(min, max),
            mat3.create()
        );
        this.applyFocus(views, bb, point, false);
    }

    public get zoomToCursor(): boolean {
        return this._zoomToCursor;
    }

    public setZoomToCursor(zoomToCursor: boolean) {
        this._zoomToCursor = zoomToCursor;
    }

    public zoomIn(view: PlaneView, center: ReadonlyVec2 | undefined) {
        if (this._scale < 10) {
            const newScale = this.scale * PanZoom.SCALE_IN;
            if (this._zoomToCursor && center) {
                view.zoomTo(view.clientToCanvas(center), newScale);
            } else {
                view.updateZoomScale(newScale);
            }
            this.scale = newScale;
        }
    }

    public zoomOut(view: PlaneView, center: ReadonlyVec2 | undefined) {
        if (this._scale > 0.4) {
            const newScale = this.scale * PanZoom.SCALE_OUT;
            if (this._zoomToCursor && center) {
                view.zoomTo(view.clientToCanvas(center), newScale);
            } else {
                view.updateZoomScale(newScale);
            }
            this.scale = newScale;
        }
    }

    /**
     * Method to get the current {@link _scale scaling} of this {@link PanZoom}-object.
     */
    public get scale(): number {
        return this._scale;
    }

    public set scale(scaling: number) {
        this._scale = scaling;
        this.notify(scaling);
        this.invalidator.invalidate(LayerBit.ALL_LAYERS);
    }
    /**
     * Method to move the {@link _translation} by some {@link ReadonlyVec3 delta vector}.
     * This also invalidates all layers of the views.
     * @param delta {@link ReadonlyVec3} that is to be added to {@link _translation translation}.
     * @returns The new {@link _translation}.
     */
    public moveCenter(delta: ReadonlyVec3): ReadonlyVec3 {
        this.invalidator.invalidate(LayerBit.ALL_LAYERS);
        return delta; // vec3.add(this._translation, this._translation, delta);
    }
}

function followsRank(view: PlaneView): number {
    return view.geometryLink?.roleOf(view) === 'follow' ? 1 : 0;
}
